import isEqual from 'lodash/isEqual'
import { CustomerDetail, CustomerSaveRequest } from './customerApi'
import { parseSalesLeadsToMap, selectedSalesLeadsToString } from './customersModel'

export type CustomerFormValues = CustomerDetail & {
  selectedSalesLeads: Record<string, boolean>
  tagsList: string[]
  createNewPropertyAutomatically: boolean
  leadSinceAtSwitch: string | null
  customerSinceAtSwitch: string | null
}

export const toCustomerFormValues = (customer: CustomerDetail): CustomerFormValues => {
  const tagsList = customer.tagsArray?.length
    ? customer.tagsArray.map(t => t.text)
    : (customer.tags ? customer.tags.split(', ').filter(Boolean) : [])

  return {
    ...customer,
    phones: customer.phones ?? [],
    emails: customer.emails ?? [],
    selectedSalesLeads: parseSalesLeadsToMap(customer.salesLead),
    tagsList,
    createNewPropertyAutomatically: (customer.properties ?? 0) === 0,
    leadSinceAtSwitch: null,
    customerSinceAtSwitch: null,
  }
}

export const effectiveSinceDate = (
  custom: string | null | undefined,
  standard: string | null | undefined,
  atSwitch?: string | null
): string | null => custom || standard || atSwitch || null

export const toCustomerSaveRequest = (
  values: CustomerFormValues
): { customer: CustomerSaveRequest; createFirstPropertyAutomatically: boolean } => {
  const {
    selectedSalesLeads,
    tagsList,
    createNewPropertyAutomatically,
    leadSinceAtSwitch,
    customerSinceAtSwitch,
    tagsArray: _tagsArray,
    ...rest
  } = values

  return {
    customer: {
      ...rest,
      salesLead: selectedSalesLeadsToString(selectedSalesLeads) || null,
      tags: tagsList.join(', '),
      customerSince: customerSinceAtSwitch ?? values.customerSince,
      leadSince: leadSinceAtSwitch ?? values.leadSince,
    },
    createFirstPropertyAutomatically: createNewPropertyAutomatically,
  }
}

const isBlank = (value: unknown) => value === null || value === undefined || value === false || value === ''

const stripBlanks = (value: any): any => {
  if (Array.isArray(value)) {
    return value.map(stripBlanks)
  }
  if (value === null || typeof value !== 'object') {
    return value
  }
  return Object.entries(value).reduce<Record<string, any>>((acc, [key, entry]) => {
    if (!isBlank(entry)) {
      acc[key] = stripBlanks(entry)
    }
    return acc
  }, {})
}

export const hasUnsavedCustomerChanges = (
  values: CustomerFormValues,
  baseline: CustomerFormValues
): boolean => !isEqual(stripBlanks(toCustomerSaveRequest(values)), stripBlanks(toCustomerSaveRequest(baseline)))
